import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { Download, FileUp, MailCheck, MailX, RefreshCw, Search, Send, ShieldCheck, Upload, UserCheck, UserX, Users, Copy as CopyIcon } from 'lucide-react';
import MainLayout from '../layout/MainLayout';
import CenterLayout from '../layout/CenterLayout';
import AppContext from '../Context/AppContext';
import { useSocket } from '../Context/SocketContext';
import { infoUser } from '../utils/auth';
import Loader from '../components/Loader';
import { Badge, Confirm, Empty, HScroll, Pager, Toast } from '../components/userProject/UpUi';
import { projectApi } from '../components/userProject/api';

const STATUS = {
    valid: { label: 'Valide', tone: 'ok' },
    invalid: { label: 'Invalide', tone: 'ko' },
    duplicate: { label: 'Doublon', tone: 'warn' },
    member: { label: 'Déjà membre', tone: 'info' },
    excluded: { label: 'Exclu (client)', tone: 'muted' },
    pending: { label: 'À vérifier', tone: 'muted' },
}
// même texte que PrimeStation (services/prospects.js MANUAL_EXCLUDE) : exclu à la main, gardé par la vérification
const MANUAL_EXCLUDE = 'exclu à la main'
const SOURCES = { empire: 'Empire', backup: 'User backup (client)', 'backup+empire': 'Empire + client backup' }
// ESPREMIUM connecté à ce tracker (là où le bouton lit user_backup) : affiché avant de lancer
const ESPREMIUM_HOST = (() => { try { return new URL(process.env.REACT_APP_PUBLIC_SOCKET_URL).host } catch { return '?' } })()
const ESPREMIUM_PROD = !/localhost|127\.0\.0\.1/.test(ESPREMIUM_HOST)
const EMAILS = /[^\s,;"'<>()[\]]+@[^\s,;"'<>()[\]]+\.[a-z]{2,}/gi
const CHUNK = 500 // lignes par envoi (le relais du tracker accepte ~100 Ko)
const day = (d) => (d ? moment(d).format('DD/MM/YY') : '—')

/**
 * Campagne d'invitation (PrimeStation) : les anciens users d'Empire à inviter — base adminempire_2020 (importée par le
 * script local « npm run prospects »). Les clients actuels (user_backup) ne sont JAMAIS invités : exclus, soit depuis un
 * CSV (« Exclure des emails »), soit depuis la liste de l'onglet Users (lue avec ta session). Un email = une ligne.
 * Chaque email vérifié sans rien envoyer : format, adresse bidon / jetable, faute de frappe (correction proposée),
 * domaine qui reçoit des mails (DNS), même boîte Gmail, déjà membre PrimeStation. Export CSV de ce qui est filtré.
 */
const Invitations = () => {
    const { setCurrentPage } = useContext(AppContext)
    const { socket } = useSocket()
    const pApi = useMemo(() => projectApi('primestation'), [])
    const [stats, setStats] = useState(null)
    const [list, setList] = useState(null)
    const [error, setError] = useState('')
    const [filters, setFilters] = useState({ status: '', source: '', reason: '', q: '', invited: '', page: 1 })
    const [search, setSearch] = useState('')
    const [busy, setBusy] = useState(null) // 'import' | 'check' | 'export'
    const [ask, setAsk] = useState(false)
    const [lastRun, setLastRun] = useState(null) // résultat de la dernière comparaison (chiffres seulement)
    const fileRef = useRef()
    const [toast, setToast] = useState(null)
    const [rowBusy, setRowBusy] = useState(null) // id de l'email en cours d'exclusion / réintégration
    const notify = useCallback((text, isError = false) => setToast({ text, error: isError, at: Date.now() }), [])

    useEffect(() => { setCurrentPage('invitations') }, [setCurrentPage])

    const loadStats = useCallback(async () => {
        try { setStats(await pApi.get('/prospects/stats')); setError('') } catch (e) { setError(e.message) }
    }, [pApi])
    const loadList = useCallback(async () => {
        const { status, source, reason, q, invited, page } = filters
        try {
            setList(await pApi.get('/prospects', { page, ...(status && { status }), ...(source && { source }), ...(reason && { reason }), ...(q && { q }), ...(invited && { invited }) }))
        } catch (e) { setError(e.message) }
    }, [pApi, filters])
    useEffect(() => { loadStats() }, [loadStats])
    useEffect(() => { loadList() }, [loadList])
    useEffect(() => {
        const t = setTimeout(() => setFilters((f) => (f.q === search.trim() ? f : { ...f, q: search.trim(), page: 1 })), 350)
        return () => clearTimeout(t)
    }, [search])
    const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value, page: 1 }))

    // exclure à la main (même un valide : il passe dans les exclus et n'est jamais invité) ou réintégrer
    const setExcluded = async (p, excluded) => {
        setRowBusy(p.id)
        try {
            await pApi.post('/prospects/exclude', { ids: [p.id], excluded })
            notify(excluded ? `${p.email} : exclu des invitations` : `${p.email} : réintégré`)
            await Promise.all([loadStats(), loadList()])
        } catch (e) { notify(e.message, true) }
        setRowBusy(null)
    }

    // clients à exclure → PrimeStation par paquets (source backup), puis tout revérifié ; résultat en chiffres (rien d'affiché)
    const sendExclusions = async (data, label) => {
        const before = (await pApi.get('/prospects/stats')).byStatus || {}
        let added = 0
        let merged = 0
        for (let i = 0; i < data.length; i += CHUNK) {
            const r = await pApi.post('/prospects/import', { source: 'backup', rows: data.slice(i, i + CHUNK), check: false })
            added += r.import.added
            merged += r.import.merged
        }
        const after = (await pApi.post('/prospects/check')).byStatus || {}
        setLastRun({
            label,
            read: data.length,
            removed: (before.valid || 0) - (after.valid || 0),
            already: merged,
            outside: added,
            validBefore: before.valid || 0,
            validAfter: after.valid || 0,
            at: Date.now(),
        })
        notify(`${label} : comparaison faite`)
        await Promise.all([loadStats(), loadList()])
    }

    // CSV (ou texte) contenant les emails des clients : chaque email trouvé est exclu
    const excludeFile = async (e) => {
        const file = e.target.files?.[0]
        e.target.value = ''
        if (!file) return
        setBusy('import')
        try {
            const text = await file.text()
            const emails = [...new Set((text.match(EMAILS) || []).map((m) => m.trim().toLowerCase()))]
            if (!emails.length) throw new Error('Aucun email trouvé dans ce fichier')
            await sendExclusions(emails.map((email) => ({ email })), file.name)
        } catch (err) { notify(err.message, true) }
        setBusy(null)
    }

    // user_backup : la liste de l'onglet Users (socket ESPREMIUM, ta session) → exclue
    const importBackup = async () => {
        setAsk(false)
        if (!socket) return notify('Socket ESPREMIUM pas connecté : recharge la page', true)
        setBusy('import')
        try {
            const rows = await new Promise((resolve, reject) => {
                const t = setTimeout(() => reject(new Error('ESPREMIUM ne répond pas')), 20000)
                socket.emit('empire_get_user_backup', { token: infoUser()?.expecttoken }, (res) => {
                    clearTimeout(t)
                    Array.isArray(res) ? resolve(res) : reject(new Error('ESPREMIUM refuse l’accès à user_backup'))
                })
            })
            await sendExclusions(rows.map((r) => ({ email: r.email, name: r.name || null, signedUpAt: r.datepay || null, premiumUntil: r.dateend || null })), 'user_backup')
        } catch (e) { notify(e.message, true) }
        setBusy(null)
    }

    const recheck = async () => {
        setBusy('check')
        try {
            const r = await pApi.post('/prospects/check')
            notify(`${r.total} emails revérifiés · ${r.changed} changés`)
            await Promise.all([loadStats(), loadList()])
        } catch (e) { notify(e.message, true) }
        setBusy(null)
    }

    const exportCsv = async () => {
        setBusy('export')
        try {
            const { status, source, reason, q, invited } = filters
            const r = await pApi.get('/prospects/export', { ...(status && { status }), ...(source && { source }), ...(reason && { reason }), ...(q && { q }), ...(invited && { invited }) })
            const url = URL.createObjectURL(new Blob([r.csv], { type: 'text/csv;charset=utf-8' }))
            const a = document.createElement('a')
            a.href = url
            a.download = r.filename
            a.click()
            setTimeout(() => URL.revokeObjectURL(url), 2000)
        } catch (e) { notify(e.message, true) }
        setBusy(null)
    }

    const copy = (text) => navigator.clipboard?.writeText(text).then(() => notify('Copié'), () => notify('Copie impossible', true))
    const s = stats?.byStatus || {}
    const pages = list ? Math.max(1, Math.ceil(list.total / list.perPage)) : 1
    const inv = stats?.invites || { sent: 0, left: 0 }
    const invPct = inv.sent + inv.left ? Math.round((inv.sent / (inv.sent + inv.left)) * 100) : 0
    const showInvited = (invited) => setFilters((f) => ({ ...f, status: invited === 'no' ? 'valid' : '', invited, page: 1 }))
    const cards = [
        ['', Users, 'Total', stats?.total, 'ta liste Empire, sans doublon'],
        ['valid', MailCheck, 'Valides', s.valid, `${(inv.left || 0).toLocaleString('fr-FR')} pas encore invités`],
        ['invalid', MailX, 'Invalides', s.invalid, 'format, bidon, jetable, domaine…'],
        ['duplicate', CopyIcon, 'Doublons', s.duplicate, 'même boîte Gmail'],
        ['member', UserCheck, 'Déjà membres', s.member, 'compte PrimeStation'],
        ['excluded', UserX, 'Exclus', s.excluded, `déjà clients (sur ${(stats?.clients?.total || 0).toLocaleString('fr-FR')} user_backup lus) + exclus à la main`],
    ]

    return (
        <MainLayout>
            <CenterLayout>
                <div className='up-page inv-page'>
                    <header className='up-head'>
                        <div>
                            <h1>Invitations</h1>
                            <p className='up-muted'>
                                Anciens users d'Empire à inviter sur PrimeStation : base adminempire_2020 (script « npm run prospects ») + user_backup.
                                Chaque email est vérifié sans rien envoyer. {stats?.lastCheck && <>Dernière vérification {moment(stats.lastCheck).fromNow()}.</>}
                            </p>
                        </div>
                        <div className='inv-actions'>
                            <button className='btn cw' onClick={() => fileRef.current?.click()} disabled={!!busy} title='CSV ou texte avec les emails de tes clients'>
                                <FileUp size={15} /> Exclure un CSV
                            </button>
                            <input ref={fileRef} type='file' accept='.csv,.txt,text/csv,text/plain' onChange={excludeFile} hidden />
                            <button className='btn cs' onClick={() => setAsk(true)} disabled={!!busy} title={`Lit la liste de l’onglet Users sur ${ESPREMIUM_HOST} (non affichée) et retire ces clients des invitations`}>
                                <Upload size={15} /> {busy === 'import' ? 'Comparaison…' : 'Récupérer user_backup et comparer'}
                            </button>
                            <button className='btn cw' onClick={recheck} disabled={!!busy}><ShieldCheck size={15} /> {busy === 'check' ? 'Vérification…' : 'Revérifier'}</button>
                            <button className='btn cw' onClick={exportCsv} disabled={!!busy || !list?.total}><Download size={15} /> {busy === 'export' ? 'Export…' : 'Exporter CSV'}</button>
                        </div>
                    </header>

                    {lastRun && (
                        <div className='inv-result'>
                            <b>{lastRun.label} : comparaison faite</b>
                            <span>{lastRun.read.toLocaleString('fr-FR')} clients lus (non affichés)</span>
                            <span><strong>{lastRun.removed.toLocaleString('fr-FR')}</strong> retirés des invitations</span>
                            <span>{lastRun.outside.toLocaleString('fr-FR')} absents de la liste Empire (gardés comme exclus)</span>
                            <span>À inviter : {lastRun.validBefore.toLocaleString('fr-FR')} → <strong>{lastRun.validAfter.toLocaleString('fr-FR')}</strong></span>
                            <button className='pay-act-btn' onClick={() => setLastRun(null)} title='Fermer'>✕</button>
                        </div>
                    )}
                    {error && <p className='up-error'>{error}</p>}
                    {!stats && !error && <div className='up-loading'><Loader /></div>}
                    {stats && (
                        <>
                            {/* marqueur : qui a reçu l'email d'invitation (campagnes « Emails »), qui reste */}
                            <div className='inv-progress'>
                                <span className='inv-progress-title'><Send size={16} /> Invitations envoyées</span>
                                <b>{inv.sent.toLocaleString('fr-FR')} <small>/ {(inv.sent + inv.left).toLocaleString('fr-FR')}</small></b>
                                <span className='inv-progress-bar'><i style={{ width: `${invPct}%` }} /></span>
                                <span className='up-muted'>{invPct} % · reste {inv.left.toLocaleString('fr-FR')}{inv.last ? ` · dernier envoi ${moment(inv.last).fromNow()}` : ''}</span>
                                <span className='inv-progress-actions'>
                                    <button className={`btn cw ${filters.invited === 'yes' ? 'on' : ''}`} onClick={() => showInvited(filters.invited === 'yes' ? '' : 'yes')}>Voir les invités</button>
                                    <button className={`btn cw ${filters.invited === 'no' ? 'on' : ''}`} onClick={() => showInvited(filters.invited === 'no' ? '' : 'no')}>Voir ceux qui restent</button>
                                </span>
                            </div>
                            <div className='inv-cards'>
                                {cards.map(([status, Icon, label, n, sub]) => (
                                    <button key={label} className={`inv-card ${status || 'all'} ${filters.status === status ? 'on' : ''}`} onClick={() => setFilter('status', status)}>
                                        <span><Icon size={17} /> {label}</span>
                                        <b>{(n || 0).toLocaleString('fr-FR')}</b>
                                        <small>{sub}</small>
                                    </button>
                                ))}
                            </div>

                            <div className='inv-side'>
                                <div className='inv-box'>
                                    <p className='inv-box-title'>Pourquoi pas valides</p>
                                    {!stats.reasons.length && <Empty>Aucun</Empty>}
                                    {stats.reasons.map((r) => (
                                        <button key={r.reason} className={`inv-reason ${filters.reason === r.reason ? 'on' : ''}`} onClick={() => setFilter('reason', filters.reason === r.reason ? '' : r.reason)}>
                                            <span>{r.reason}</span><b>{r.n.toLocaleString('fr-FR')}</b>
                                        </button>
                                    ))}
                                </div>
                                <div className='inv-box'>
                                    <p className='inv-box-title'>Domaines <span>· valides / total</span></p>
                                    {stats.domains.map((d) => (
                                        <div key={d.domain} className='inv-domain'>
                                            <span>{d.domain}</span>
                                            <i style={{ '--p': `${Math.round((d.valid / Math.max(1, d.n)) * 100)}%` }} />
                                            <b>{d.valid.toLocaleString('fr-FR')} / {d.n.toLocaleString('fr-FR')}</b>
                                        </div>
                                    ))}
                                </div>
                                <div className='inv-box'>
                                    <p className='inv-box-title'>Sources</p>
                                    {Object.entries(stats.bySource).map(([k, n]) => (
                                        <button key={k} className={`inv-reason ${filters.source && k.includes(filters.source) ? 'on' : ''}`} onClick={() => setFilter('source', filters.source === k.split('+')[0] ? '' : k.split('+')[0])}>
                                            <span>{SOURCES[k] || k}</span><b>{n.toLocaleString('fr-FR')}</b>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}

                    <div className='up-toolbar'>
                        <div className='up-search'>
                            <Search size={16} />
                            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder='Email…' />
                        </div>
                        <select value={filters.status} onChange={(e) => setFilter('status', e.target.value)}>
                            <option value=''>Statut : tous</option>
                            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                        <select value={filters.source} onChange={(e) => setFilter('source', e.target.value)}>
                            <option value=''>Source : toutes</option>
                            <option value='empire'>Empire (adminempire_2020)</option>
                            <option value='backup'>User backup (clients)</option>
                        </select>
                        <select value={filters.invited} onChange={(e) => setFilter('invited', e.target.value)}>
                            <option value=''>Invitation : tous</option>
                            <option value='yes'>Déjà invités</option>
                            <option value='no'>Pas encore invités</option>
                        </select>
                        {filters.reason && <button className='btn cw' onClick={() => setFilter('reason', '')}>Raison : {filters.reason} ✕</button>}
                        <button className='pay-act-btn' onClick={() => { loadStats(); loadList() }} title='Recharger'><RefreshCw size={16} color='#fff' /></button>
                    </div>

                    {list && <p className='up-muted inv-count'>{list.total.toLocaleString('fr-FR')} email{list.total > 1 ? 's' : ''}</p>}
                    {!list && !error && <div className='up-loading'><Loader /></div>}
                    {list && !list.items.length && <Empty>Aucun email pour ces filtres</Empty>}
                    {list?.items.length > 0 && (
                        <HScroll>
                            <div className='inv-table'>
                                <div className='inv-row head'>
                                    <span>Email</span><span>Statut</span><span>Raison / correction</span><span>Invitation</span><span>Source</span><span>Inscrit Empire</span><span>Premium Empire</span><span>Action</span>
                                </div>
                                {list.items.map((p) => (
                                    <div key={p.id} className='inv-row'>
                                        <span className='inv-email' title={p.email}>
                                            {p.email}
                                            <button className='inv-copy' onClick={() => copy(p.email)} title='Copier'><CopyIcon size={13} /></button>
                                        </span>
                                        <span><Badge tone={STATUS[p.status]?.tone}>{p.reason === MANUAL_EXCLUDE ? 'Exclu (à la main)' : STATUS[p.status]?.label || p.status}</Badge></span>
                                        <span className='inv-reason-cell'>
                                            {p.reason || '—'}
                                            {p.suggestion && <small>→ {p.suggestion}</small>}
                                        </span>
                                        <span className='inv-sent'>
                                            {p.invitedAt ? <><Badge tone='ok'>Invité {day(p.invitedAt)}</Badge>{p.inviteCode && <small>{p.inviteCode}</small>}</> : '—'}
                                        </span>
                                        <span>{SOURCES[p.sources] || p.sources}</span>
                                        <span>{day(p.signedUpAt)}</span>
                                        <span>{p.premiumUntil ? day(p.premiumUntil) : '—'}</span>
                                        <span>
                                            {p.status !== 'excluded' && (
                                                <button className='btn cw inv-act' disabled={rowBusy === p.id} onClick={() => setExcluded(p, true)} title='Ne jamais inviter cet email'>
                                                    <UserX size={14} /> Exclure
                                                </button>
                                            )}
                                            {p.reason === MANUAL_EXCLUDE && (
                                                <button className='btn cw inv-act' disabled={rowBusy === p.id} onClick={() => setExcluded(p, false)} title='Le remettre dans la liste (revérifié)'>
                                                    <UserCheck size={14} /> Réintégrer
                                                </button>
                                            )}
                                            {p.status === 'excluded' && p.reason !== MANUAL_EXCLUDE && <span className='up-muted'>—</span>}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </HScroll>
                    )}
                    {list && pages > 1 && <Pager page={filters.page} pages={pages} onPage={(page) => setFilters((f) => ({ ...f, page }))} />}
                </div>
                {ask && (
                    <Confirm
                        title='Récupérer user_backup et comparer'
                        text={`La liste user_backup est lue sur ${ESPREMIUM_HOST}${ESPREMIUM_PROD ? ' (prod)' : ' (local)'} avec ta session, sans être affichée. Ces clients sont retirés des invitations (même boîte Gmail écrite autrement comprise) ; tu vois seulement les chiffres.`}
                        action='Récupérer et comparer'
                        onConfirm={importBackup}
                        onClose={() => setAsk(false)}
                    />
                )}
                <Toast toast={toast} onDone={() => setToast(null)} />
            </CenterLayout>
        </MainLayout>
    );
};

export default Invitations;
